import { useDroppable } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { useCallback, useMemo } from 'react';
import { Content } from 'src/contexts/ContentContext';
import { Stack } from 'src/core/ui';
import { useReportV2ColumnPrecision } from 'src/hooks/useColumnUserSetting';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FormatOption, FormatOptionPill } from 'src/modals/EditTableColumns';
import {
  getCustomColumnDisplayName,
  isCustomColumnName,
} from 'src/utils/columns/reportV2CustomColumnUtils';
import { ContentId } from 'src/utils/constants/contentId';
import {
  getTagDisplayNameFromReportingColumnName,
  isTagReportingColumnName,
  ReportTypesV2,
  tagReportingColumnNameToBadgeContentId,
} from 'src/utils/reportsUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { Feature } from 'src/WebApiController';

import { getIsPercentFlagForReportColumn } from './MetricsInputDroppable.utils';
import { SortableMetricsOptionsListItem } from './MetricsOptionsListItem';
import * as styles from './ReportsTableEditor.css';
import { REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES } from './ReportsTableEditorForm.constants';

export const MetricsInputDroppable = ({
  id,
  valueOptionsContent,
  selectedValues,
  disabled,
  requiredColumnNames,
  reportType,
}: {
  id: string;
  valueOptionsContent: Record<string, ContentId>;
  disabled?: boolean;
  selectedValues: string[];
  requiredColumnNames?: string[] | undefined;
  reportType: ReportTypesV2;
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: id,
  });

  const hasReportV2ColumnFormatFeature = useUserHasFeature(
    Feature.ReportV2ColumnFormat
  );

  const { userDefinedPrecisions, updateNumberPrecisionSetting } =
    useReportV2ColumnPrecision(reportType);

  const personalizationConfigOverrides = useMemo(() => {
    return (
      REPORT_TYPE_TO_COLUMN_PERSONALIZATION_CONFIG_OVERRIDES[reportType] ?? {}
    );
  }, [reportType]);

  const renderColumnRowActions = useCallback(
    (columnId: string) => {
      const precision = userDefinedPrecisions?.[columnId];

      const { isNumeric, isDateTime, isInteger, isCurrency } =
        personalizationConfigOverrides[columnId] ?? {};

      const isCustomColumn = isCustomColumnName(columnId);
      const isPercent = getIsPercentFlagForReportColumn({
        columnId,
        personalizationConfigOverrides,
        precision,
      });

      return (
        <Stack
          direction="row"
          gap="s"
          alignItems="center"
          // Avoid the click event from propagating to the parent droppable
          onPointerDown={(e) => e.stopPropagation()}
        >
          {isCustomColumn ? (
            <div className={styles.badge}>
              <Content id={ContentId.Custom} />
            </div>
          ) : isTagReportingColumnName(columnId) ? (
            <div className={styles.badge}>
              <Content id={tagReportingColumnNameToBadgeContentId(columnId)} />
            </div>
          ) : requiredColumnNames?.includes(columnId) ? (
            <div className={styles.badge}>
              <Content id={ContentId.Required} />
            </div>
          ) : null}

          {hasReportV2ColumnFormatFeature &&
          (isNumeric || isDateTime || isCustomColumn) ? (
            <FormatOptionPill
              id={columnId}
              onSave={updateNumberPrecisionSetting}
              option={precision ?? (isInteger ? FormatOption.Whole : undefined)}
              flags={{
                isDateTime,
                isCurrency,
                isPercent,
                isCustomColumn,
              }}
              isSelectable={true}
              isSelected
              isCompact
              // This prop is not important
              sectionType={SectionType.ListingsReport}
            />
          ) : null}
        </Stack>
      );
    },
    [
      hasReportV2ColumnFormatFeature,
      personalizationConfigOverrides,
      requiredColumnNames,
      updateNumberPrecisionSetting,
      userDefinedPrecisions,
    ]
  );

  return (
    <SortableContext items={selectedValues}>
      <Stack
        direction="column"
        height="full"
        width="full"
        ref={setNodeRef}
        style={{ overflowY: 'auto' }}
      >
        <Stack direction="column" height="full" width="full" gap="s">
          {selectedValues.map((value) => (
            <SortableMetricsOptionsListItem
              key={value}
              id={value}
              disabled={disabled}
              actionButton={renderColumnRowActions(value)}
            >
              {isCustomColumnName(value) ? (
                <span>{getCustomColumnDisplayName(value)}</span>
              ) : isTagReportingColumnName(value) ? (
                <span>{getTagDisplayNameFromReportingColumnName(value)}</span>
              ) : (
                <Content id={valueOptionsContent[value]} />
              )}
            </SortableMetricsOptionsListItem>
          ))}
        </Stack>
      </Stack>
    </SortableContext>
  );
};
